/**
 * sld-style.js
 *
 * Loads an SLD via OGC API - Styles, parses with @nieuwlandgeo/sldreader,
 * and produces an OL style function plus a hoisted layer-level opacity.
 *
 * Opacity flattening (per the styles plan):
 *   - hoistedOpacity = the FIRST rule's first symbolizer's fill-opacity,
 *     defaulting to 1.0 if absent. Stroke-opacity is ignored. Per-rule
 *     variation is collapsed by always reading the first rule.
 *   - Every fill-opacity / stroke-opacity is then stripped from every
 *     rule × every symbolizer before passing the structure to
 *     createOlStyleFunction, so the layer's opacity slider isn't
 *     fighting baked-in alpha. The opacity slider in LegendControl
 *     becomes the single source of truth for layer opacity.
 *   - sld-edge-cases.js can override both behaviours per-rule (e.g. a
 *     transparent-fill polygon hoists from stroke-opacity instead, and
 *     opts out of the fill-opacity strip).
 *
 * Returns: { styleFn, hoistedOpacity, ruleSummaries, classificationAttributes }
 */

import { Reader, createOlStyleFunction } from '@nieuwlandgeo/sldreader';
import { evaluateRule } from './sld-edge-cases';

const DEFAULT_OPACITY = 1.0;

// EPSG:3857 metres-per-pixel → SLD scale denominator. Standard pixel is
// 0.28 mm so denom = resolution / 0.00028 ≈ resolution × 3571. The 90.7
// figure in earlier sketches was wrong; QGIS-exported SLDs use the
// OGC standard pixel definition.
const SCALE_FROM_RESOLUTION = 1 / 0.00028;

export async function loadSldStyle(url) {
  const xml = await fetch(url).then(function (r) {
    if (!r.ok) throw new Error('SLD fetch returned ' + r.status);
    return r.text();
  });

  const sld = Reader(xml);
  const ftStyle = _firstFeatureTypeStyle(sld);
  if (!ftStyle) throw new Error('SLD has no usable FeatureTypeStyle');

  // Evaluate edge cases once per rule, in order. Subsequent passes
  // (summarise, strip, hoist) all consult this map so the three views
  // stay consistent: a rule treated as transparent-fill-visible-stroke
  // for hoisted opacity is also rendered as a line in the map key
  // AND kept out of the fill-opacity strip step.
  const evaluations = _evaluateRules(ftStyle);

  const hoistedOpacity = _deriveHoistedOpacity(ftStyle, evaluations);
  // Snapshot rule summaries (for the map key) *before* stripping opacity
  // so the key can faithfully indicate per-rule alpha if a future SLD
  // varies it. The styleFn pass below mutates the structure.
  const ruleSummaries = _summarizeRules(ftStyle, evaluations);
  const classificationAttributes = _extractClassificationAttributes(ftStyle);
  _stripOpacity(ftStyle, evaluations);

  const styleFn = createOlStyleFunction(ftStyle, {
    convertResolution: function (r) { return r * SCALE_FROM_RESOLUTION; }
  });

  return {
    styleFn: styleFn,
    hoistedOpacity: hoistedOpacity,
    ruleSummaries: ruleSummaries,
    classificationAttributes: classificationAttributes
  };
}

// Walk every rule's filter tree and return the set of attribute names
// the filters reference, in first-seen order. For graduated/categorized
// symbology this is typically a single attribute (e.g. ['pop_2000']);
// styles that combine multiple attributes will surface all of them.
// Returns [] for filterless styles ("style everything the same way").
function _extractClassificationAttributes(ftStyle) {
  const seen = new Set();
  const out = [];

  function visitFilter(node) {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'and' || node.type === 'or') {
      (node.predicates || []).forEach(visitFilter);
      return;
    }
    if (node.type === 'not') {
      visitFilter(node.predicate);
      return;
    }
    // comparison / not-binary-logical → inspect its expressions.
    // sldreader uses expression1/expression2 plus, on some predicates,
    // lowerboundary/upperboundary (PropertyIsBetween).
    ['expression1', 'expression2', 'lowerboundary', 'upperboundary']
      .forEach(function (k) { visitExpression(node[k]); });
  }

  function visitExpression(expr) {
    if (!expr || typeof expr !== 'object') return;
    if (expr.type === 'propertyname' && expr.value) {
      if (!seen.has(expr.value)) {
        seen.add(expr.value);
        out.push(expr.value);
      }
    }
  }

  for (const rule of (ftStyle.rules || [])) {
    visitFilter(rule.filter);
  }
  return out;
}

// Build a parallel array of edge-case evaluations, one per rule (null
// when no handler matched). Kept separate from the rule list itself so
// the sldreader structure stays untouched — handlers describe
// *adjustments*, they don't mutate.
function _evaluateRules(ftStyle) {
  const rules = ftStyle.rules || [];
  return rules.map(function (rule) { return evaluateRule(rule); });
}

// Lightweight per-rule descriptor for the map-key UI. Captures the
// rule's first symbolizer (sufficient for graduated/categorized
// styles); multi-symbolizer rules are summarised by their first.
// When an edge case applies to a rule, its summary override is merged
// on top of the default — e.g. a transparent-fill polygon is recast
// as a line swatch so the map key shows the stroke.
function _summarizeRules(ftStyle, evaluations) {
  const rules = ftStyle.rules || [];
  return rules.map(function (rule, i) {
    const sym = (rule.symbolizers || [])[0] || null;
    const fillSt = sym && sym.fill && sym.fill.styling;
    const strokeSt = sym && sym.stroke && sym.stroke.styling;
    const base = {
      label: (rule.title && rule.title.trim()) ||
             (rule.name && rule.name.trim()) ||
             '(unnamed)',
      symbolType: sym ? sym.type : null,
      fill: fillSt ? fillSt.fill : null,
      fillOpacity: fillSt ? fillSt.fillOpacity : null,
      stroke: strokeSt ? strokeSt.stroke : null,
      strokeWidth: strokeSt ? strokeSt.strokeWidth : null
    };
    const ev = evaluations && evaluations[i];
    if (ev && ev.summary) {
      return Object.assign(base, ev.summary);
    }
    return base;
  });
}

function _firstFeatureTypeStyle(sld) {
  const layer = sld && sld.layers && sld.layers[0];
  if (!layer) return null;
  const style = layer.styles && layer.styles[0];
  if (!style) return null;
  const ftStyles = style.featuretypestyles || [];
  return ftStyles[0] || null;
}

// Layer-level opacity. Default per the styles plan: "first rule's
// first symbolizer's fill-opacity, defaulting to 1.0 if absent."
// Stroke-opacity is intentionally not consulted by the default path.
//
// Edge cases get first crack: the first rule whose evaluation supplies
// a hoistedOpacityHint wins. The transparent-fill-visible-stroke case
// uses this to source the layer opacity from stroke-opacity instead,
// so the slider matches what the user actually sees on the map (the
// stroke) rather than the deliberately-invisible fill.
function _deriveHoistedOpacity(ftStyle, evaluations) {
  const rules = ftStyle.rules || [];
  for (let i = 0; i < rules.length; i++) {
    const ev = evaluations && evaluations[i];
    if (ev && ev.hoistedOpacityHint != null) {
      return Number(ev.hoistedOpacityHint);
    }
  }
  for (let i = 0; i < rules.length; i++) {
    const syms = rules[i].symbolizers || [];
    for (let j = 0; j < syms.length; j++) {
      const op = syms[j] && syms[j].fill && syms[j].fill.styling
                 && syms[j].fill.styling.fillOpacity;
      if (op != null) return Number(op);
    }
  }
  return DEFAULT_OPACITY;
}

// Edge-case-aware strip. Default behaviour is unchanged — both
// fill-opacity and stroke-opacity get removed so the layer slider
// becomes the single source of opacity. The transparent-fill-visible-
// stroke case opts out of the fill strip (preserveFillOpacity=true):
// stripping a 0 fill-opacity would let OL re-default it to 1.0 and
// the layer slider would never bring it back to zero, breaking the
// see-through-but-clickable behaviour the SLD was authored for.
function _stripOpacity(ftStyle, evaluations) {
  const rules = ftStyle.rules || [];
  for (let i = 0; i < rules.length; i++) {
    const ev = evaluations && evaluations[i];
    const skipFill = !!(ev && ev.preserveFillOpacity);
    const syms = rules[i].symbolizers || [];
    for (let j = 0; j < syms.length; j++) {
      const sym = syms[j];
      if (!skipFill && sym && sym.fill && sym.fill.styling) {
        delete sym.fill.styling.fillOpacity;
      }
      if (sym && sym.stroke && sym.stroke.styling) {
        delete sym.stroke.styling.strokeOpacity;
      }
    }
  }
}
